import React, { createContext, useContext, useReducer, ReactNode } from 'react'

export type SetupDeviceType = 'belt' | 'cradle'

export interface DeviceLabels {
  type: SetupDeviceType
  singular: string
  titleCase: string
  productName: string
  article: string
  apiLabel: string
}

export const DEVICE_LABELS: Record<SetupDeviceType, DeviceLabels> = {
  belt: {
    type: 'belt',
    singular: 'belt',
    titleCase: 'Belt',
    productName: 'Aurora Care Belt',
    article: 'your Aurora Care belt',
    apiLabel: 'belt',
  },
  cradle: {
    type: 'cradle',
    singular: 'cradle',
    titleCase: 'Cradle',
    productName: 'Aurora Care Cradle',
    article: 'your Aurora Care cradle',
    apiLabel: 'cradle',
  },
}

interface SetupState {
  deviceType: SetupDeviceType
  deviceUid: string
  deviceId: number | null
  provisioningToken: string
  expiresAt: string
}

type SetupAction =
  | {
      type: 'SET_PROVISIONING_DATA'
      payload: Omit<SetupState, 'deviceType'>
    }
  | { type: 'SET_DEVICE_TYPE'; payload: SetupDeviceType }
  | { type: 'RESET' }

const initialState: SetupState = {
  deviceType: 'belt',
  deviceUid: '',
  deviceId: null,
  provisioningToken: '',
  expiresAt: '',
}

function setupReducer(state: SetupState, action: SetupAction): SetupState {
  switch (action.type) {
    case 'SET_PROVISIONING_DATA':
      return {
        ...state,
        ...action.payload,
      }
    case 'SET_DEVICE_TYPE':
      return {
        ...state,
        deviceType: action.payload,
      }
    case 'RESET':
      return initialState
    default:
      return state
  }
}

interface SetupContextType extends SetupState {
  labels: DeviceLabels
  setProvisioningData: (data: Omit<SetupState, 'deviceType'>) => void
  setDeviceType: (type: SetupDeviceType) => void
  reset: () => void
}

const SetupContext = createContext<SetupContextType | undefined>(undefined)

export function SetupProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(setupReducer, initialState)

  const setProvisioningData = (data: Omit<SetupState, 'deviceType'>) => {
    dispatch({ type: 'SET_PROVISIONING_DATA', payload: data })
  }

  const setDeviceType = (type: SetupDeviceType) => {
    dispatch({ type: 'SET_DEVICE_TYPE', payload: type })
  }

  const reset = () => {
    dispatch({ type: 'RESET' })
  }

  return React.createElement(
    SetupContext.Provider,
    {
      value: {
        ...state,
        labels: DEVICE_LABELS[state.deviceType],
        setProvisioningData,
        setDeviceType,
        reset,
      },
    },
    children,
  )
}

export function useSetup() {
  const context = useContext(SetupContext)
  if (context === undefined) {
    throw new Error('useSetup must be used within a SetupProvider')
  }
  return context
}
